import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLang, pick } from "@/contexts/LanguageContext";
import VehicleCard from "@/components/cars/VehicleCard";
import { api } from "@/lib/api";

export default function Catalog({ vehicles, settings }) {
  const { lang, t } = useLang();
  const [filter, setFilter] = useState("all");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/vehicles/categories"); setCategories(data || []); }
      catch { /* ignore */ }
    })();
  }, [vehicles?.length]);

  const filtered = useMemo(() => {
    if (filter === "all") return vehicles;
    return vehicles.filter((v) => v.categoryKey === filter);
  }, [vehicles, filter]);

  return (
    <section id="catalog" className="bg-[#0C0C0C] py-20 md:py-32" data-testid="catalog-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-px bg-[#C9A55A]" />
              <span className="text-overline text-[#C9A55A]">{t.catalog.kicker}</span>
            </div>
            <h2 className="font-display text-[#F5F1EB] text-3xl md:text-5xl max-w-2xl" data-testid="catalog-title">
              {t.catalog.title}
            </h2>
            <p className="mt-4 text-[#F5F1EB]/70 font-light max-w-xl">{t.catalog.subtitle}</p>
          </div>
        </div>

        <div className="flex gap-2 md:gap-3 overflow-x-auto no-scrollbar pb-3 mb-10 -mx-6 md:mx-0 px-6 md:px-0" data-testid="catalog-filters">
          <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
            {t.catalog.filterAll}
          </FilterPill>
          {categories.map((c) => (
            <FilterPill key={c.key} active={filter === c.key} onClick={() => setFilter(c.key)} testid={`filter-${c.key}`}>
              {pick(c.label, lang) || c.key}
            </FilterPill>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 text-[#8A8A8A]" data-testid="catalog-empty">{t.catalog.empty}</div>
        ) : (
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            data-testid="catalog-grid">
            {filtered.map((v, idx) => (
              <motion.div
                key={v.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: (idx % 6) * 0.06 }}>
                <VehicleCard vehicle={v} settings={settings} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`shrink-0 px-4 md:px-5 h-10 text-xs tracking-[0.18em] uppercase font-semibold border transition-all ${
        active ? "border-[#C9A55A] text-[#0C0C0C] bg-[#C9A55A]" : "border-[#2A2A2A] text-[#F5F1EB] hover:border-[#C9A55A] hover:text-[#C9A55A]"
      }`}>
      {children}
    </button>
  );
}
